import {Component, DestroyRef, inject, OnInit} from '@angular/core';
import {
  Aangifte,
  AangifteRapport,
  BokkzIbDataService,
  IbRekenService,
  IncomeTaxEntity
} from '@snelstart-ventures/fiscaal/domain-logic';
import {BehaviorSubject, filter, Observable, shareReplay, take} from 'rxjs';
import {FormControl, FormGroup} from '@angular/forms';
import {SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {CommonModule, DecimalPipe} from '@angular/common';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

@Component({
    selector: 'snelstart-venture-fiscaal-feature-zorgverzekeringswet',
    templateUrl: './zorgverzekeringswet.component.html',
    imports: [SharedUiFormControlsModule, SharedUiDetailsModule, DecimalPipe, CommonModule],
    styleUrls: ['./zorgverzekeringswet.component.scss'],
})
export class ZorgverzekeringswetComponent implements OnInit {
    private dataService = inject(BokkzIbDataService);
    private destroyRef = inject(DestroyRef);
    private bokkzClient = inject(IbRekenService);
    public dataVisible$ = new BehaviorSubject<boolean>(false);
    public isPartner = false;
    private rapportRequestId = 0;

    public formGroup = new FormGroup({
        loonHogerDanMaximumBijdrageInkomen: new FormControl<boolean | null>(null),
        loonBuitenlandVoorZvw: new FormControl<number | null>(0),
        vrijgesteldLoonVoorZvw: new FormControl<number | null>(0),
        loonAlsOnderdeelVanWinst: new FormControl<number | null>(0),
        inLijfrenteOmgezetteForBijStaking: new FormControl<number | null>(0),
        inLijfrenteOmgezetteForNietBijStaking: new FormControl<number | null>(0),
        bijdrageInkomen: new FormControl<number | null>(0),
        loonWaaroverIsGeheven: new FormControl<number | null>(0),

        winstUitOnderneming: new FormControl<number | null>(0),
        resultaatUitOverigeWerkzaamheden: new FormControl<number | null>(0),
        periodiekeUitkeringen: new FormControl<number | null>(0),

        maxPremieLoon: new FormControl<number | null>(0),
        totaalBijdrageInkomenNaMaxEnLoon: new FormControl<number | null>(0),

        verschuldigdeZvw: new FormControl<number | null>(0),
        percentageZvw: new FormControl<number | null>(0),
        voorlopigeAanslagZvw: new FormControl<number | null>(0),
        verschuldigdePremie: new FormControl<number | null>(0),
    });

    readonly incomeTaxEntity$: Observable<IncomeTaxEntity> = this.dataService.incomeTaxEntity$.pipe(
        filter((data): data is IncomeTaxEntity => !!data?.sstRelationId),
        takeUntilDestroyed(this.destroyRef),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    ngOnInit(): void {
        this.incomeTaxEntity$.pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe();
        this.dataService.partnerAangifte$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((isPartner) => {
            this.isPartner = isPartner;
            const aangifte = this.dataService.incomeTaxEntity.value.aangifte;
            if (!aangifte) {
                this.dataVisible$.next(false);
                return;
            }
            this.getRapportIfChanged(aangifte, isPartner);
        });
    }

    public getRapportIfChanged(aangifte: Aangifte, partner: boolean) {
        const requestId = ++this.rapportRequestId;
        this.dataVisible$.next(false);

        if (partner) {
            this.bokkzClient.getIncomeTaxReport(aangifte.partnerAangifte!).subscribe((rapport) => {
                if (requestId !== this.rapportRequestId) {
                    return;
                }
                this.resetAndPatchForms(rapport);
            });
        } else {
            this.bokkzClient.getIncomeTaxReport(aangifte).subscribe((rapport) => {
                if (requestId !== this.rapportRequestId) {
                    return;
                }
                this.resetAndPatchForms(rapport);
            });
        }
    }

    private resetAndPatchForms(details: AangifteRapport): void {
        this.formGroup.reset({}, { emitEvent: false });

        this.formGroup.patchValue(details.specificatieZvw?.opbouw || {}, { emitEvent: false });
        this.formGroup.patchValue(
            { winstUitOnderneming: details.specificatieInkomenBox1?.opbouw?.winstUitOnderneming ?? 0 },
            { emitEvent: false },
        );
        this.formGroup.patchValue(
            {
                resultaatUitOverigeWerkzaamheden:
                    (details.specificatieROW?.opbouw?.inkomstenUitOVerigeWerkzaamheden ?? 0) -
                    (details.specificatieROW?.opbouw?.kostenTerZakeVanOverigeWerkzaamheden ?? 0),
            },
            { emitEvent: false },
        );
        this.formGroup.patchValue(
            { periodiekeUitkeringen: details.specificatieInkomenBox1?.opbouw?.periodiekeUitkeringen ?? 0 },
            { emitEvent: false },
        );
        this.formGroup.patchValue(
            {
                bijdrageInkomen: Math.min(
                    (details.specificatieZvw?.opbouw?.bijdrageInkomen ?? 0) + (details.specificatieZvw?.opbouw?.loonWaaroverIsGeheven ?? 0),
                    details.specificatieWetgeving?.maxBijdrageInkomenZvw ?? 0,
                ),
            },
            { emitEvent: false },
        );
        this.formGroup.patchValue({ maxPremieLoon: details.specificatieWetgeving?.maxBijdrageInkomenZvw ?? 0 }, { emitEvent: false });
        this.formGroup.patchValue(
            {
                voorlopigeAanslagZvw: this.isPartner
                    ? (this.dataService.incomeTaxEntity.value.aangifte?.partnerAangifte?.voorheffingen?.voorlopigeAanslagZvw ?? 0)
                    : (this.dataService.incomeTaxEntity.value.aangifte?.voorheffingen?.voorlopigeAanslagZvw ?? 0),
            },
            { emitEvent: false },
        );
        this.formGroup.patchValue(
            {
                verschuldigdeZvw:
                    (details.specificatieZvw?.opbouw?.verschuldigdePremie ?? 0) + (this.formGroup.controls.voorlopigeAanslagZvw.value ?? 0),
            },
            { emitEvent: false },
        );
        this.formGroup.patchValue({ percentageZvw: details.specificatieWetgeving?.percentageZvw ?? 0 }, { emitEvent: false });

        this.formGroup.disable({ emitEvent: false });
        this.dataVisible$.next(true);
    }

    get grondslagZvwPremie(): any {
        const totaalBijdrageInkomen = this.formGroup.controls.bijdrageInkomen.value ?? 0;
        const inkomenWaaroverIsGeheven = this.formGroup.controls.loonWaaroverIsGeheven.value ?? 0;
        const sum = Math.max(totaalBijdrageInkomen - inkomenWaaroverIsGeheven, 0);
        this.formGroup.patchValue({ totaalBijdrageInkomenNaMaxEnLoon: sum }, { emitEvent: false });
        return sum;
    }
}
